import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { IconPlus } from '@tabler/icons-react'

const PIZZA_SVG = (color) => (
  <svg width="11" height="11" viewBox="0 0 28 28" fill="none">
    <path d="M14 4 L26 22 Q14 27 2 22 Z" stroke={color} strokeWidth="2.2" fill="none" strokeLinejoin="round"/>
    <path d="M14 4 Q20 13 26 22" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M14 4 Q8 13 2 22" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M6 19 Q14 24 22 19" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round"/>
    <circle cx="11" cy="15.5" r="1.8" fill={color}/>
    <circle cx="17" cy="13" r="1.8" fill={color}/>
  </svg>
)

const TEXTURES = [
  (t) => <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}><rect width="100%" height="100%" fill={t.border}/><circle cx="20%" cy="30%" r="2" fill={t.header} opacity=".2"/><circle cx="45%" cy="55%" r="1.5" fill={t.header} opacity=".18"/><circle cx="70%" cy="25%" r="2.5" fill={t.darkHeader} opacity=".14"/><circle cx="30%" cy="70%" r="1.5" fill={t.header} opacity=".18"/><circle cx="80%" cy="65%" r="1" fill={t.darkHeader} opacity=".22"/></svg>,
  (t) => <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice"><rect width="100" height="100" fill={t.border}/><ellipse cx="22" cy="20" rx="14" ry="9" fill={t.header} opacity=".2" transform="rotate(-15 22 20)"/><ellipse cx="62" cy="38" rx="18" ry="10" fill={t.darkHeader} opacity=".15" transform="rotate(10 62 38)"/><ellipse cx="82" cy="18" rx="10" ry="7" fill={t.header} opacity=".18" transform="rotate(20 82 18)"/></svg>,
  (t) => <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}><defs><radialGradient id="bw1" cx="30%" cy="40%" r="60%"><stop offset="0%" stopColor={t.border} stopOpacity=".95"/><stop offset="100%" stopColor={t.header} stopOpacity=".3"/></radialGradient></defs><rect width="100%" height="100%" fill={t.border}/><ellipse cx="30%" cy="40%" rx="60%" ry="55%" fill="url(#bw1)" opacity=".8"/></svg>,
  (t) => <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}><defs><pattern id="bws" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse"><rect width="8" height="8" fill={t.border}/><circle cx="1.5" cy="1.5" r=".7" fill={t.header} opacity=".2"/><circle cx="5.5" cy="4" r=".5" fill={t.darkHeader} opacity=".16"/></pattern></defs><rect width="100%" height="100%" fill="url(#bws)"/></svg>,
  (t) => <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice"><rect width="100" height="100" fill={t.border}/><circle cx="50" cy="50" r="10" fill="none" stroke={t.header} strokeWidth="1.5" opacity=".3"/><circle cx="50" cy="50" r="22" fill="none" stroke={t.header} strokeWidth="1.2" opacity=".25"/><circle cx="50" cy="50" r="36" fill="none" stroke={t.header} strokeWidth="1" opacity=".2"/><circle cx="50" cy="50" r="52" fill="none" stroke={t.header} strokeWidth=".8" opacity=".15"/></svg>,
  (t) => <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{position:'absolute',inset:0}}><defs><pattern id="bwc" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse"><rect width="8" height="8" fill={t.border}/><line x1="0" y1="8" x2="8" y2="0" stroke={t.header} strokeWidth=".6" opacity=".22"/><line x1="0" y1="0" x2="8" y2="8" stroke={t.header} strokeWidth=".6" opacity=".22"/></pattern></defs><rect width="100%" height="100%" fill="url(#bwc)"/></svg>,
]

export default function Bisqueware({ theme: t, onSelectPiece }) {
  const [pieces, setPieces] = useState([])
  const [glazePlans, setGlazePlans] = useState({})
  const [glazes, setGlazes] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('plan')
  const [expandedPiece, setExpandedPiece] = useState(null)
  const [glazeNames, setGlazeNames] = useState([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [piecesRes, plansRes, glazesRes] = await Promise.all([
      supabase.from('pieces').select('*').eq('firing_stage', 'bisque-fire').order('created_at', { ascending: false }),
      supabase.from('glaze_plans').select('*'),
      supabase.from('glazes').select('*').order('name'),
    ])
    if (piecesRes.data) setPieces(piecesRes.data)
    if (plansRes.data) {
      const plansMap = {}
      plansRes.data.forEach(p => { plansMap[p.piece_id] = p })
      setGlazePlans(plansMap)
    }
    if (glazesRes.data) {
      setGlazes(glazesRes.data)
      setGlazeNames(glazesRes.data.map(g => g.name))
    }
    setLoading(false)
  }

  const saveGlazePlan = async (pieceId, updatedPlan) => {
    const existing = glazePlans[pieceId]
    const planToSave = { ...updatedPlan, piece_id: pieceId }
    if (existing?.id) {
      await supabase.from('glaze_plans').update(planToSave).eq('id', existing.id)
    } else {
      const { data } = await supabase.from('glaze_plans').insert(planToSave).select().single()
      if (data) setGlazePlans(prev => ({ ...prev, [pieceId]: data }))
    }
    setGlazePlans(prev => ({ ...prev, [pieceId]: { ...existing, ...planToSave } }))
    // Update glaze master index
    const allGlazes = [...(updatedPlan.exterior_layers || []), ...(updatedPlan.interior_layers || [])].filter(Boolean)
    for (const name of allGlazes) {
      await supabase.from('glazes').upsert({ name }, { onConflict: 'name' })
    }
  }

  const getFirstGlaze = (pieceId) => {
    const plan = glazePlans[pieceId]
    return plan?.exterior_layers?.[0] || null
  }

  const getGlazeColor = (glazeName) => {
    if (!glazeName) return null
    const glaze = glazes.find(g => g.name === glazeName)
    return glaze?.color_hex || null
  }

  const getPlanSummary = (pieceId) => {
    const plan = glazePlans[pieceId]
    if (!plan) return null
    const ext = (plan.exterior_layers || []).filter(Boolean)
    const int = (plan.interior_layers || []).filter(Boolean)
    if (ext.length === 0) return null
    if (plan.has_separate_interior && int.length > 0) return `Ext: ${ext.join(' + ')} · Int: ${int.join(' + ')}`
    return ext.join(' + ')
  }

  // Group pieces by first glaze layer for Glaze Day tab
  const groupedByGlaze = pieces.reduce((acc, piece) => {
    const firstGlaze = getFirstGlaze(piece.id) || 'No glaze planned'
    if (!acc[firstGlaze]) acc[firstGlaze] = []
    acc[firstGlaze].push(piece)
    return acc
  }, {})

  const plannedPieces = pieces.filter(p => getPlanSummary(p.id))
  const unplannedPieces = pieces.filter(p => !getPlanSummary(p.id))

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: t.textLight, fontFamily: 'Jost, sans-serif', fontSize: '13px' }}>Loading...</div>

  return (
    <div style={{ fontFamily: 'Jost, sans-serif', background: t.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', paddingBottom: '80px' }}>

      {/* HEADER */}
      <div style={{ background: t.header, padding: '48px 16px 14px' }}>
        <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '28px', fontWeight: '700', color: '#e8f8f6', lineHeight: 1, marginBottom: '2px' }}>On the Shelf</div>
        <div style={{ fontSize: '10px', fontWeight: '300', color: t.textMuted, marginBottom: '14px' }}>
          {pieces.length} pieces ready · {unplannedPieces.length} need glaze plans
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {['plan', 'glaze-day'].map(tab => (
            <div key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '8px', borderRadius: '12px', fontSize: '12px', fontWeight: '400', textAlign: 'center', cursor: 'pointer', background: activeTab === tab ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.07)', color: activeTab === tab ? '#e8f8f6' : t.textMuted }}>
              {tab === 'plan' ? 'Plan' : 'Glaze Day'}
            </div>
          ))}
        </div>
      </div>

      {/* PLAN TAB */}
      {activeTab === 'plan' && (
        <div style={{ padding: '12px 12px 24px' }}>
          {pieces.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '24px', color: t.header, marginBottom: '8px' }}>Nothing on the shelf yet</div>
              <div style={{ fontSize: '13px', color: t.textLight }}>Move pieces to Bisque-fire to see them here</div>
            </div>
          ) : (
            <>
              {plannedPieces.length > 0 && (
                <>
                  <div style={{ fontSize: '9px', fontWeight: '500', letterSpacing: '1.2px', textTransform: 'uppercase', color: t.textLight, margin: '4px 0 8px' }}>Glaze planned</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                    {plannedPieces.map(piece => (
                      <PieceCard key={piece.id} piece={piece} plan={glazePlans[piece.id]} glazes={glazes} t={t} expandedPiece={expandedPiece} setExpandedPiece={setExpandedPiece} saveGlazePlan={saveGlazePlan} glazeNames={glazeNames} onSelectPiece={onSelectPiece} getPlanSummary={getPlanSummary} getGlazeColor={getGlazeColor}/>
                    ))}
                  </div>
                </>
              )}
              {unplannedPieces.length > 0 && (
                <>
                  <div style={{ fontSize: '9px', fontWeight: '500', letterSpacing: '1.2px', textTransform: 'uppercase', color: t.textLight, margin: '4px 0 8px' }}>Needs glaze plan</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {unplannedPieces.map(piece => (
                      <PieceCard key={piece.id} piece={piece} plan={glazePlans[piece.id]} glazes={glazes} t={t} expandedPiece={expandedPiece} setExpandedPiece={setExpandedPiece} saveGlazePlan={saveGlazePlan} glazeNames={glazeNames} onSelectPiece={onSelectPiece} getPlanSummary={getPlanSummary} getGlazeColor={getGlazeColor} needsPlan/>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* GLAZE DAY TAB */}
      {activeTab === 'glaze-day' && (
        <div style={{ padding: '12px 12px 24px' }}>
          {pieces.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '24px', color: t.header, marginBottom: '8px' }}>Nothing on the shelf yet</div>
            </div>
          ) : (
            Object.entries(groupedByGlaze).map(([glazeName, groupPieces]) => (
              <div key={glazeName} style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  {getGlazeColor(glazeName) && <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: getGlazeColor(glazeName), flexShrink: 0 }}/>}
                  <div style={{ fontSize: '11px', fontWeight: '500', color: t.text }}>{glazeName}</div>
                  <div style={{ fontSize: '10px', fontWeight: '300', color: t.textLight }}>· {groupPieces.length} piece{groupPieces.length !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {groupPieces.map(piece => (
                    <div key={piece.id} onClick={() => onSelectPiece(piece.id)} style={{ background: t.card, borderRadius: '14px', overflow: 'hidden', border: `1px solid ${t.border}`, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
                      <div style={{ height: '80px', position: 'relative', overflow: 'hidden' }}>
                        {piece.default_photo_url ? <img src={piece.default_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : TEXTURES[(piece.texture_index || 1) - 1](t)}
                        {/* Glaze pips */}
                        {glazePlans[piece.id] && (
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', display: 'flex', gap: '3px', background: 'rgba(30,79,88,.6)' }}>
                            {(glazePlans[piece.id].exterior_layers || []).filter(Boolean).map((layer, i) => (
                              <div key={i} style={{ height: '5px', borderRadius: '3px', flex: 1, background: getGlazeColor(layer) || t.textMuted }}/>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '7px 9px 9px' }}>
                        <div style={{ fontFamily: 'Alike, serif', fontSize: '10px', color: t.text, marginBottom: '2px' }}>{piece.unique_id}</div>
                        <div style={{ fontSize: '9px', fontWeight: '300', color: t.textLight }}>{getPlanSummary(piece.id) || 'No plan'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function PieceCard({ piece, plan, glazes, t, expandedPiece, setExpandedPiece, saveGlazePlan, glazeNames, onSelectPiece, getPlanSummary, getGlazeColor, needsPlan }) {
  const isExpanded = expandedPiece === piece.id
  const summary = getPlanSummary(piece.id)
  const localPlan = plan || { has_separate_interior: false, exterior_layers: [''], interior_layers: [''] }

  return (
    <div style={{ background: t.card, borderRadius: '14px', overflow: 'hidden', border: needsPlan ? `1.5px dashed ${t.accent}` : `1px solid ${t.border}`, boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
      <div onClick={() => setExpandedPiece(isExpanded ? null : piece.id)} style={{ cursor: 'pointer' }}>
        <div style={{ height: '80px', position: 'relative', overflow: 'hidden', background: needsPlan ? `${t.accent}20` : 'transparent' }}>
          {piece.default_photo_url ? <img src={piece.default_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : <div style={{ position: 'relative', height: '100%' }}>{TEXTURES[(piece.texture_index || 1) - 1](t)}</div>}
          {needsPlan && <div style={{ position: 'absolute', top: '7px', left: '7px', background: t.accent, color: t.accentText, fontSize: '9px', fontWeight: '500', borderRadius: '8px', padding: '2px 7px' }}>No plan yet</div>}
          {!needsPlan && plan && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', display: 'flex', gap: '3px', background: 'rgba(30,79,88,.6)' }}>
              {(plan.exterior_layers || []).filter(Boolean).map((layer, i) => (
                <div key={i} style={{ height: '5px', borderRadius: '3px', flex: 1, background: getGlazeColor(layer) || t.textMuted }}/>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: '7px 9px 6px' }}>
          <div style={{ fontFamily: 'Alike, serif', fontSize: '10px', color: t.text, marginBottom: '2px' }}>{piece.unique_id}</div>
          <div style={{ fontSize: '9px', fontWeight: '300', color: needsPlan ? t.accent : t.textLight }}>{summary || 'Tap to plan →'}</div>
        </div>
      </div>

      {/* Expanded glaze editor */}
      {isExpanded && (
        <div style={{ padding: '8px 10px 10px', borderTop: `1px solid ${t.border}` }}>
          <div style={{ fontSize: '9px', fontWeight: '500', letterSpacing: '.8px', textTransform: 'uppercase', color: t.textLight, marginBottom: '8px' }}>Exterior layers</div>
          {(localPlan.exterior_layers || ['']).map((layer, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <div style={{ fontSize: '10px', color: t.textLight, width: '14px' }}>{i + 1}.</div>
              <input
                value={layer}
                onChange={e => {
                  const layers = [...(localPlan.exterior_layers || [''])]
                  layers[i] = e.target.value
                  const updated = { ...localPlan, exterior_layers: layers }
                  saveGlazePlan(piece.id, updated)
                }}
                placeholder="Glaze name..."
                list="bw-glaze-suggestions"
                style={{ flex: 1, background: t.bg, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '6px 8px', fontSize: '11px', color: t.text, outline: 'none', fontFamily: 'Jost, sans-serif' }}
              />
            </div>
          ))}
          <datalist id="bw-glaze-suggestions">{glazeNames.map(n => <option key={n} value={n}/>)}</datalist>
          <div onClick={() => {
            const updated = { ...localPlan, exterior_layers: [...(localPlan.exterior_layers || ['']), ''] }
            saveGlazePlan(piece.id, updated)
          }} style={{ fontSize: '10px', color: t.header, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginTop: '4px' }}>
            <IconPlus size={11}/> Add layer
          </div>
          <div onClick={() => onSelectPiece(piece.id)} style={{ marginTop: '10px', padding: '6px', borderRadius: '8px', background: t.header, color: '#e8f8f6', fontSize: '10px', fontWeight: '400', textAlign: 'center', cursor: 'pointer' }}>
            Open full card →
          </div>
        </div>
      )}
    </div>
  )
}